import React from "react";
import {Card, Chip, ChipType, Colors, Container, IconLitigation, Text} from "../../../../ui";
import {IconApplePay, IconCreditCard, IconDiscover, IconMastercard, IconVisa} from "../../../bounce-ui";
import {PaymentType} from "../../../declarations";
import {DEFAULT_PAYMENT_METHODS_ORDER, getShouldShowApplePay, PaymentMethodOption} from "./utils";

type Props = {
  selected: PaymentMethodOption;
  onSelect: (method: PaymentMethodOption) => void;
  isAgent?: boolean;
  achAllowed?: boolean;
  showAch?: boolean;
  cardDetailsTitle: string;
  applePayEligible?: boolean;
  merchantSiteId?: string;
  achChip?: {label: string; variant: ChipType["variant_"]} | null;
  methods?: PaymentMethodOption[];
  achBlockedAfterFailure?: boolean;
};

const Radio = ({checked}: {checked: boolean}) => (
  <Container sx={{gap: "6px"}}>
    <Container sx={{position: "relative", width: 16, height: 16}}>
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `1px solid ${checked ? Colors.primary._800 : Colors.grey._600}`,
        }}
      />
      {checked && (
        <div
          style={{
            position: "absolute",
            left: 4,
            top: 4,
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: Colors.primary._800,
          }}
        />
      )}
    </Container>
  </Container>
);

type PaymentMethodCardProps = {
  paymentType: PaymentType;
  isChecked: boolean;
  text: string;
  icon: React.ReactNode;
  chip?: {
    label: string;
    variant: ChipType["variant_"];
    border?: boolean;
  };
  onClick: () => void;
};

const PaymentMethodCard = ({paymentType, isChecked, text, icon, chip, onClick}: PaymentMethodCardProps) => (
  <Card
    sx={{
      padding: "16px",
      borderRadius: "16px",
      border: `${isChecked ? "2px" : "1px"} solid ${isChecked ? Colors.primary._800 : Colors.tertiary._300}`,
    }}
    onClick={onClick}
  >
    <Container row sx={{gap: "8px", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
      <Container sx={{gap: "4px", alignItems: "flex-start"}}>
        <Container row sx={{gap: "8px", alignItems: "center"}}>
          <Radio checked={isChecked} />
          <Text variant="inter600">{text}</Text>
          {chip && <Chip label={chip.label} variant_={chip.variant} />}
        </Container>
        {isChecked && paymentType === "DebitCard" && (
          <Container row sx={{marginLeft: "30px", gap: "4px"}}>
            <IconVisa /> <IconMastercard /> <IconDiscover />
          </Container>
        )}
      </Container>
      {icon}
    </Container>
  </Card>
);

const PaymentMethodSelector: React.FC<Props> = ({
  selected,
  onSelect,
  isAgent = false,
  showAch = true,
  cardDetailsTitle,
  applePayEligible,
  merchantSiteId,
  achChip,
  methods = DEFAULT_PAYMENT_METHODS_ORDER,
  achBlockedAfterFailure = false,
}) => {
  const shouldShowApplePay = getShouldShowApplePay(isAgent, applePayEligible, merchantSiteId);

  const methodCards: Partial<Record<PaymentMethodOption, React.ReactNode>> = {
    ApplePay: shouldShowApplePay && (
      <PaymentMethodCard
        paymentType={"ApplePay"}
        isChecked={selected === "ApplePay"}
        text="Apple Pay"
        icon={<IconApplePay />}
        onClick={() => onSelect("ApplePay")}
      />
    ),
    ACH:
      showAch &&
      (achBlockedAfterFailure ? (
        <div style={{opacity: 0.5, pointerEvents: "none", width: "100%"}}>
          <PaymentMethodCard
            paymentType={"ACH"}
            isChecked={selected === "ACH"}
            text="ACH"
            icon={<IconLitigation />}
            chip={{label: "Unavailable", variant: "error"}}
            onClick={() => {}}
          />
        </div>
      ) : (
        <PaymentMethodCard
          paymentType={"ACH"}
          isChecked={selected === "ACH"}
          text="ACH"
          icon={<IconLitigation />}
          chip={achChip ? {label: achChip.label, variant: achChip.variant} : undefined}
          onClick={() => onSelect("ACH")}
        />
      )),
    DebitCard: (
      <PaymentMethodCard
        paymentType={"DebitCard"}
        isChecked={selected === "DebitCard"}
        text={cardDetailsTitle}
        icon={<IconCreditCard />}
        onClick={() => onSelect("DebitCard")}
      />
    ),
  };

  return (
    <Container fullWidth sx={{gap: "8px"}}>
      {methods.map((method) => (
        <React.Fragment key={method}>{methodCards[method]}</React.Fragment>
      ))}
    </Container>
  );
};

export {PaymentMethodSelector};
